import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Download, Film } from 'lucide-react';
import { recordingApi } from '@/features/recordings/recording.api';

function formatBytes(b: number): string {
  if (!b) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(1)} ${units[i]}`;
}

function formatDuration(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${sec}s`;
}

export default function RecordingsPage(): JSX.Element {
  const { data, isLoading } = useQuery({ queryKey: ['recordings'], queryFn: () => recordingApi.list() });

  const onDownload = async (id: string) => {
    try {
      const url = await recordingApi.download(id);
      window.open(url, '_blank');
    } catch {
      toast.error('Recording not ready yet');
    }
  };

  const recordings = data?.recordings ?? [];

  return (
    <div>
      <h1 className="text-2xl font-bold">Recordings</h1>
      <div className="mt-6 space-y-3">
        {isLoading && <p className="text-sm text-slate-500">Loading…</p>}
        {!isLoading && recordings.length === 0 && (
          <div className="card text-sm text-slate-500">No recordings yet. Start a recording from inside a meeting.</div>
        )}
        {recordings.map((r) => (
          <div key={r.id} className="card flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Film className="text-brand-600" />
              <div>
                <p className="font-medium">Recording · {new Date(r.createdAt).toLocaleString()}</p>
                <p className="text-sm text-slate-500">
                  {r.status} · {formatDuration(r.durationSecs)} · {formatBytes(r.fileSize)}
                </p>
              </div>
            </div>
            <button
              className="btn-primary px-3 py-1.5 text-sm disabled:opacity-50"
              disabled={r.status !== 'COMPLETED'}
              onClick={() => onDownload(r.id)}
            >
              <Download size={14} /> Download
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
